import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UIDialog } from 'src/components/ui/Modal'
import { useAgeVerificationStatus } from 'src/services/helpers/preference'
import { track } from 'src/utils/tracker'
import { UIButton } from '../ui/Button'

/**
 * Prompt user to choose age restriction option
 *
 * @returns User choice
 *          - true: restriction lifted
 *          - false: restriction kept
 *          - void: dialog closed without choice
 */
export function checkAgeRestriction() {
  return new Promise<boolean | void>(resolve => {
    UIDialog.create({
      children: ({ close }) => (
        <AdultRestrictionWarnDialogContent
          onClose={() => {
            resolve()
            close()
          }}
          onRestrictionLifted={() => {
            resolve(true)
            close()
          }}
          onRestrictionKept={() => {
            resolve(false)
            close()
          }}
        />
      ),
    })
  })
}

function AdultRestrictionWarnDialogContent(props: {
  onClose?: () => void
  onRestrictionLifted?: () => void
  onRestrictionKept?: () => void
}) {
  const { t } = useTranslation(['generation', 'action'])
  const [, setAgeRestriction, loading] = useAgeVerificationStatus()
  return (
    <>
      <DialogTitle>
        {t('generation:submit.age-verification-dialog.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText className="sm:min-w-80">
          {t('generation:submit.age-verification-dialog.description')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <UIButton
          variant="outlined"
          onPress={async () => {
            track('age_restriction_cancel_click')
            props.onClose?.()
          }}
        >
          {t('action:cancel')}
        </UIButton>
        <UIButton
          variant="filled"
          onPress={async () => {
            track('under_18_click')
            await setAgeRestriction('minor')
            props.onRestrictionKept?.()
          }}
          busy={loading}
        >
          {t('generation:submit.age-verification-dialog.under-18-label')}
        </UIButton>
        <UIButton
          variant="filled"
          onPress={async () => {
            track('over_18_click')
            await setAgeRestriction('adult')
            props.onRestrictionLifted?.()
          }}
          busy={loading}
        >
          {t('generation:submit.age-verification-dialog.over-18-label')}
        </UIButton>
      </DialogActions>
    </>
  )
}
